import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { appel, ecrireJeton, heure, lireJeton, quandNonAutorise, type Compte, type EvenementPasse, type EvenementVue } from "./api.js";
import { Ico, Messager, useLecture, useMaintenant } from "./commun.js";
import { PageVille } from "./ville.js";
import { PageRegie } from "./evenement.js";
import "./regie.css";
import { ESPACES_HUMAINS } from "../espaces.js";

/**
 * La régie (maquette « Régie — refonte »).
 *
 * Deux espaces, deux boutons en haut à gauche :
 *   - **Ville** : les quartiers, la B2C. Réservée au compte Alunites (et à
 *     l'entrée d'exploitation historique) ;
 *   - **Régie** : les événements du compte (B2B) — un à la fois, ses tables,
 *     son direct, ses bilans, sa voix.
 *
 * On y entre avec un identifiant et un mot de passe. Chaque compte a sa
 * propre régie. Pas de version téléphone : c'est un écran de bureau.
 */

export type Route =
  | { espace: "ville"; page: "soir" | "fil" | "signalements" | "reglages" | "adresses" | "textes" | "systeme"; id?: string }
  | {
      espace: "regie";
      page: "evenements" | "direct" | "fil" | "tables" | "table" | "plan" | "parametres" | "textes" | "zone" | "annonceur" | "signalements" | "bilan";
      id?: string;
      onglet?: string;
    };

const PAGES_VILLE = ["soir", "fil", "signalements", "reglages", "adresses", "textes", "systeme"];
const PAGES_REGIE = ["evenements", "direct", "fil", "tables", "table", "plan", "parametres", "textes", "zone", "annonceur", "signalements", "bilan"];

function lireRoute(): Route | null {
  const [espace, page, id, onglet] = location.hash.replace(/^#\/?/, "").split("/");
  if (espace === "ville" && PAGES_VILLE.includes(page ?? "")) return { espace, page: page as never, id };
  if (espace === "regie" && PAGES_REGIE.includes(page ?? "")) return { espace, page: page as never, id, onglet };
  return null;
}

export function versHash(r: Route): string {
  const morceaux: string[] = [r.espace, r.page];
  if (r.id) morceaux.push(r.id);
  if (r.espace === "regie" && r.onglet) morceaux.push(r.onglet);
  return `#/${morceaux.join("/")}`;
}

interface Contexte {
  compte: Compte;
  majCompte: (c: Compte) => void;
  route: Route;
  aller: (r: Route) => void;
  evenement: EvenementVue | null;
  passes: EvenementPasse[];
  relireEvenements: () => Promise<void>;
  signalements: { ville: number; regie: number };
  relireCompteurs: () => Promise<void>;
}

const Ctx = createContext<Contexte | null>(null);
export function useRegie(): Contexte {
  const c = useContext(Ctx);
  if (!c) throw new Error("hors de la régie");
  return c;
}

export function Regie(): JSX.Element {
  const [jeton, setJeton] = useState<string | null>(lireJeton);
  const [compte, setCompte] = useState<Compte | null>(null);

  useEffect(() => {
    document.title = ESPACES_HUMAINS ? "Régie Espaces Humains" : "Régie Hoa";
    quandNonAutorise(() => {
      ecrireJeton(null);
      setJeton(null);
      setCompte(null);
    });
    return () => quandNonAutorise(null);
  }, []);

  if (!jeton) {
    return (
      <div className="rg">
        <Connexion
          onEntree={(t, c) => {
            ecrireJeton(t);
            setJeton(t);
            setCompte(c);
            if (!lireRoute()) location.hash = c?.ville === false || ESPACES_HUMAINS ? "#/regie/evenements" : "#/ville/soir";
          }}
        />
      </div>
    );
  }
  return <Espace compte={compte} onCompte={setCompte} />;
}

function Espace(props: { compte: Compte | null; onCompte: (c: Compte) => void }): JSX.Element {
  const moi = useLecture(() => appel<{ compte: Compte; signalements: { ville: number; regie: number } }>("/api/regie/moi"), 15_000);
  const compte = moi.donnees?.compte ?? props.compte;
  const [route, setRoute] = useState<Route | null>(lireRoute);
  useEffect(() => {
    const f = (): void => setRoute(lireRoute());
    window.addEventListener("hashchange", f);
    return () => window.removeEventListener("hashchange", f);
  }, []);
  const aller = useCallback((r: Route) => {
    location.hash = versHash(r);
  }, []);

  const ev = useLecture(() => appel<{ courant: EvenementVue | null; passes: EvenementPasse[] }>("/api/regie/evenements"), 10_000);
  // Changer d'écran relit l'événement : ce qu'on vient de faire ailleurs doit s'y voir.
  const relireEv = ev.relire;
  useEffect(() => {
    void relireEv();
  }, [route?.espace, route?.page, relireEv]);

  if (!compte) {
    return (
      <div className="rg">
        <div className="entree">
          <p className="sub">{moi.erreur ?? "Chargement…"}</p>
        </div>
      </div>
    );
  }

  // Sans l'accès ville, la ville n'existe pas pour ce compte.
  // Espaces Humains (hu.hoa.city) : la régie des stands seulement, pas la ville.
  const ville = compte.ville && !ESPACES_HUMAINS;
  const r: Route = route && (route.espace === "regie" || ville) ? route : ville ? { espace: "ville", page: "soir" } : { espace: "regie", page: "evenements" };

  const ctx: Contexte = {
    compte,
    majCompte: props.onCompte,
    route: r,
    aller,
    evenement: ev.donnees?.courant ?? null,
    passes: ev.donnees?.passes ?? [],
    relireEvenements: ev.relire,
    signalements: moi.donnees?.signalements ?? { ville: 0, regie: 0 },
    relireCompteurs: moi.relire,
  };

  return (
    <Ctx.Provider value={ctx}>
      <div className="rg">
        {r.espace === "ville" ? <NavVille /> : <NavRegie />}
        {r.espace === "ville" ? <PageVille /> : <PageRegie />}
        <Messager />
      </div>
    </Ctx.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* Entrée                                                              */
/* ------------------------------------------------------------------ */

function Connexion(props: { onEntree: (jeton: string, compte: Compte | null) => void }): JSX.Element {
  const [identifiant, setIdentifiant] = useState("");
  const [mdp, setMdp] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const entrer = async (): Promise<void> => {
    setEnvoi(true);
    setErreur(null);
    try {
      /* Sans identifiant, c'est l'entrée d'exploitation historique : le code
       * à six chiffres ou le mot de passe de `secrets.env`. La console et les
       * recettes automatiques passent par là ; l'écran ne la montre pas. */
      if (!identifiant.trim()) {
        const r = await fetch("/api/regie/login", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ code: mdp }),
        });
        const c = (await r.json().catch(() => ({}))) as { token?: string; error?: string };
        if (!r.ok || !c.token) throw new Error(r.status === 401 ? "Identifiant ou mot de passe incorrect." : (c.error ?? "Refusé."));
        props.onEntree(c.token, null);
        return;
      }
      const r = await fetch("/api/regie/connexion", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ identifiant: identifiant.trim(), motDePasse: mdp }),
      });
      const c = (await r.json().catch(() => ({}))) as { token?: string; compte?: Compte; error?: string };
      if (!r.ok || !c.token) throw new Error(c.error ?? "Refusé.");
      props.onEntree(c.token, c.compte ?? null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "Refusé.");
      setEnvoi(false);
    }
  };

  return (
    <form
      className="entree"
      onSubmit={(e) => {
        e.preventDefault();
        void entrer();
      }}
    >
      {ESPACES_HUMAINS ? (
        <span className="logo-texte">
          espaces<b>humains</b>
        </span>
      ) : (
        <img className="logo" src="/logo/hoa-gris.svg" alt="" />
      )}
      <h1 style={{ margin: 0, font: "700 34px 'Atkinson Hyperlegible', sans-serif" }}>{ESPACES_HUMAINS ? "Régie" : "Régie Hoa"}</h1>
      <div className="champ">
        <label htmlFor="rg-id">Identifiant</label>
        <input id="rg-id" className="in" value={identifiant} autoComplete="username" autoFocus onChange={(e) => setIdentifiant(e.target.value)} />
      </div>
      <div className="champ">
        <label htmlFor="rg-mdp">Mot de passe</label>
        <input id="rg-mdp" className="in" type="password" value={mdp} autoComplete="current-password" onChange={(e) => setMdp(e.target.value)} />
      </div>
      {erreur ? (
        <p className="erreur" role="alert" style={{ alignSelf: "stretch", margin: 0 }}>
          {erreur}
        </p>
      ) : null}
      <button type="submit" className="btn p" style={{ alignSelf: "stretch", height: 52 }} disabled={envoi || !mdp}>
        Entrer
      </button>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Navigation                                                          */
/* ------------------------------------------------------------------ */

function Bascule(): JSX.Element | null {
  const { compte, route, aller } = useRegie();
  if (!compte.ville || ESPACES_HUMAINS) return null;
  return (
    <div className="bascule" role="group" aria-label="Espace">
      <button type="button" className={route.espace === "ville" ? "on" : ""} aria-current={route.espace === "ville"} onClick={() => aller({ espace: "ville", page: "soir" })}>
        Ville
      </button>
      <button
        type="button"
        className={route.espace === "regie" ? "on" : ""}
        aria-current={route.espace === "regie"}
        onClick={() => aller({ espace: "regie", page: "evenements" })}
      >
        Régie
      </button>
    </div>
  );
}

function Lien(props: { actif: boolean; onClick: () => void; children: React.ReactNode; sous?: boolean }): JSX.Element {
  return (
    <a
      href="#"
      className={[props.sous ? "sous" : "", props.actif ? "on" : ""].join(" ").trim()}
      aria-current={props.actif ? "page" : undefined}
      onClick={(e) => {
        e.preventDefault();
        props.onClick();
      }}
    >
      {props.children}
    </a>
  );
}

function NavVille(): JSX.Element {
  const { route, aller, signalements } = useRegie();
  const maintenant = useMaintenant(20_000);
  const p = route.page;
  return (
    <nav className="nav" aria-label="Ville">
      <Bascule />
      <Lien actif={p === "soir" || p === "fil"} onClick={() => aller({ espace: "ville", page: "soir" })}>
        <Ico nom="soir" />
        Ce soir
      </Lien>
      <Lien actif={p === "signalements"} onClick={() => aller({ espace: "ville", page: "signalements" })}>
        <Ico nom="signal" />
        Signalements
        {signalements.ville > 0 ? <span className="pill">{signalements.ville}</span> : null}
      </Lien>
      <Lien actif={p === "reglages" || p === "adresses" || p === "textes"} onClick={() => aller({ espace: "ville", page: "reglages" })}>
        <Ico nom="reglages" />
        Réglages
      </Lien>
      <Lien actif={p === "systeme"} onClick={() => aller({ espace: "ville", page: "systeme" })}>
        <Ico nom="systeme" />
        Système
      </Lien>
      <div className="pied">
        <span className="vivant">
          <i />
          En direct · {heure(maintenant)}
        </span>
        <button
          type="button"
          className="btn s"
          style={{ alignSelf: "stretch", padding: "0 12px" }}
          onClick={() => aller(p === "fil" ? { espace: "ville", page: "soir" } : { espace: "ville", page: "fil" })}
        >
          <Ico nom="fil" />
          {p === "fil" ? "Revenir au tableau" : ESPACES_HUMAINS ? "Le fil de l'événement" : "Le fil de la soirée"}
        </button>
      </div>
    </nav>
  );
}

function NavRegie(): JSX.Element {
  const { route, aller, evenement, compte, signalements } = useRegie();
  const p = route.page;
  const etat = useMemo(() => {
    if (!evenement) return "";
    if (evenement.ouvert) return "en cours";
    return Date.now() < evenement.debut ? "en préparation" : "terminé";
  }, [evenement]);
  return (
    <nav className="nav" aria-label="Régie">
      <Bascule />
      {evenement ? (
        <>
          <div className="groupe">
            {evenement.nom} · {etat}
          </div>
          <Lien sous actif={p === "direct" || p === "fil"} onClick={() => aller({ espace: "regie", page: "direct" })}>
            En direct
          </Lien>
          <Lien sous actif={p === "tables" || p === "table"} onClick={() => aller({ espace: "regie", page: "tables" })}>
            Tables
          </Lien>
          <Lien sous actif={p === "plan"} onClick={() => aller({ espace: "regie", page: "plan" })}>
            Plan du stand
          </Lien>
          <Lien sous actif={p === "parametres" || p === "textes"} onClick={() => aller({ espace: "regie", page: "parametres" })}>
            Paramètres
          </Lien>
          <Lien sous actif={p === "zone"} onClick={() => aller({ espace: "regie", page: "zone" })}>
            {ESPACES_HUMAINS ? "Écran et QR code" : "Zone et supports"}
          </Lien>
          <div className="separe" />
        </>
      ) : null}
      <Lien actif={p === "evenements" || p === "bilan"} onClick={() => aller({ espace: "regie", page: "evenements" })}>
        <Ico nom="evenements" />
        Événements
      </Lien>
      <Lien actif={p === "annonceur"} onClick={() => aller({ espace: "regie", page: "annonceur" })}>
        <Ico nom="annonceur" />
        Marque
      </Lien>
      <Lien actif={p === "signalements"} onClick={() => aller({ espace: "regie", page: "signalements" })}>
        <Ico nom="signal" />
        Signalements
        {signalements.regie > 0 ? <span className="pill">{signalements.regie}</span> : null}
      </Lien>
      <div className="pied">
        {evenement ? (
          <button
            type="button"
            className="btn s"
            style={{ alignSelf: "stretch", padding: "0 12px" }}
            onClick={() => aller(p === "fil" ? { espace: "regie", page: "direct" } : { espace: "regie", page: "fil" })}
          >
            <Ico nom="fil" />
            {p === "fil" ? "Revenir au tableau" : ESPACES_HUMAINS ? "Le fil de l'événement" : "Le fil de la soirée"}
          </button>
        ) : null}
        <span style={{ color: "#FFFFFF", fontWeight: 700 }}>{compte.identifiant}</span>
        <button
          type="button"
          className="lien"
          style={{ alignSelf: "flex-start" }}
          onClick={() => {
            void appel("/api/regie/deconnexion", { method: "POST" }).catch(() => undefined);
            ecrireJeton(null);
            location.hash = "";
            location.reload();
          }}
        >
          Se déconnecter
        </button>
      </div>
    </nav>
  );
}
